import {
  BodyText,
  Button,
  Flex,
  Heading,
  SectionHeader,
} from "@utilitywarehouse/hearth-react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBooleanFlagValue } from "@utilitywarehouse/feature-flags-react";

import { useAuthContext } from "../../context/auth/useAuthContext";
import { useVulnerabilityContext } from "../../context/vulnerability/useVulnerabilityContext";
import { MappedAnswers } from "../../context/vulnerability/VulnerabilityContext";
import NavigationButtons from "../../components/Questions/NavigationButtons";
import { ABOUT_ROUTES, PATHS } from "../../config/routeConfig";
import QuesionsAnswersLayout from "../../components/QuestionsAnswersLayout";
import { mapAnswerSummary } from "../utils";
import { useCustomerIsEligibleForPsr } from "../../hooks/useCustomerIsEligibleForPsr";
import { checkTokenExpiryOnInterval } from "../../context/auth/Token";
import Unauthenticated from "../../components/Unauthenticated";
import Features from "../../middleware/featureFlags/constants/features";
import { useSubmitAssessmentCustomer } from "../../graphql/customer/useSubmitAssessmentCustomer";
import Loader from "../../components/Loader";
import { SubmissionDataTest } from "../Submission/constants";

import { SummaryDataTest } from "./constants";

const Summary: React.FC = () => {
  const { setAuthenticated, authenticated } = useAuthContext();
  const { answers, setAnswers, setSubmitted } = useVulnerabilityContext();
  const navigate = useNavigate();
  const hasAbilityToOptIntoPSR = useBooleanFlagValue(Features.psrOptIn, false);
  const {
    submitAssessment,
    loading: submitLoading,
    error: submitError,
  } = useSubmitAssessmentCustomer();

  const mappedAnswers = useMemo((): MappedAnswers => {
    return mapAnswerSummary(answers);
  }, [answers]);

  const isCustomerEligibleForPSR = useCustomerIsEligibleForPsr();

  useEffect(() => {
    if (hasAbilityToOptIntoPSR) {
      setAnswers({
        ...answers,
        psr: {
          psrOutcome: {
            eligible: isCustomerEligibleForPSR,
            optedIn: false,
          },
        },
      });
    }
  }, []);

  const handleSubmission = async () => {
    if (isCustomerEligibleForPSR) {
      navigate(`/${PATHS.OptForPSR}`);
    } else {
      // if no answers, redirect
      if (!answers.children) {
        navigate(`/${PATHS.About}/${ABOUT_ROUTES.Children}`);
        return;
      }

      const result = await submitAssessment();
      if (result && !result.errors) {
        setSubmitted(true);
        navigate(`/${PATHS.Submission}`);
      }
    }
  };

  useEffect(() => {
    checkTokenExpiryOnInterval(authenticated, setAuthenticated);
  }, []);

  if (submitLoading) {
    return <Loader />;
  }

  return (
    <Flex direction="column" marginTop="150">
      {!authenticated ? (
        <Unauthenticated />
      ) : (
        <Flex direction="column" spacing="lg">
          {submitError ? (
            <>
              <BodyText>Something went wrong</BodyText>

              <BodyText color="secondary">
                Something went wrong when submitting your answers. Please click
                below to try again, or come back later to try again.
              </BodyText>
              <Button
                onClick={handleSubmission}
                variant="solid"
                color="secondary"
                data-testid={SubmissionDataTest.TRY_AGAIN_BUTTON}
                style={{ marginTop: "16px", maxWidth: "fit-content" }}
              >
                Try Again
              </Button>
            </>
          ) : (
            <Flex direction="column" gap="500" spacing="2xl">
              <Flex direction="column" spacing="lg">
                <Heading
                  size="lg"
                  data-testid={SummaryDataTest.SUMMARY_HEADER_CUSTOMER}
                >
                  Summary of your answers
                </Heading>

                <BodyText>
                  We really appreciate you taking the time to fill out this
                  survey and we hope that we're able to make your life that
                  little bit easier. Please read through your answers carefully
                  before submitting.
                </BodyText>
              </Flex>

              <Flex
                direction="column"
                gap="175"
                spacing="lg"
                data-testid={SummaryDataTest.SUMMARY_CONTAINER}
              >
                <SectionHeader
                  heading="Answer Summary"
                  data-testid={SummaryDataTest.SUMMARY_TITLE}
                />
                <QuesionsAnswersLayout mappedAnswers={mappedAnswers} />
              </Flex>
            </Flex>
          )}

          <NavigationButtons
            onBack={() => navigate(-1)}
            onContinue={handleSubmission}
            forwardName={isCustomerEligibleForPSR ? "Continue" : "Submit"}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default Summary;
